import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Upload, Send, Plug, TestTube } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const SmtpTestForm = () => {
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const send = async () => {
    if (!email) { toast.error("E-Mail-Adresse eingeben"); return; }
    setLoading(true);
    try { await api.post("/branding/smtp-test", { to_email: email }); toast.success("Test-Mail versendet"); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setLoading(false); }
  };
  return (
    <div className="pt-3 border-t border-border/60">
      <Label className="text-sm mb-1 block">Test-E-Mail senden</Label>
      <div className="flex gap-2">
        <Input data-testid="smtp-test-email" placeholder="empfaenger@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button data-testid="smtp-test-btn" variant="outline" onClick={send} disabled={loading}><Send className="w-4 h-4 mr-2" /> Test senden</Button>
      </div>
      <p className="text-xs text-slate-500 mt-1">Tipp: Erst Konfiguration speichern, dann testen.</p>
    </div>
  );
};

export default function BrandingPage() {
  const { branding, reload } = useBranding();
  const [form, setForm] = useState(null);

  useEffect(() => { if (branding) setForm(branding); }, [branding]);

  const readFile = (e, key) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm({ ...form, [key]: r.result });
    r.readAsDataURL(f);
  };

  const save = async () => {
    try {
      await api.put("/branding", form);
      toast.success("Branding gespeichert");
      await reload();
    } catch (err) { toast.error("Fehler"); }
  };

  if (!form) return null;

  return (    <div className="space-y-6" data-testid="branding-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Verwaltung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Branding & Einstellungen</h2>
        </div>
        <Button data-testid="branding-save" onClick={save} className="rounded-full"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
      </div>

      <Tabs defaultValue="visual">
        <TabsList>
          <TabsTrigger data-testid="tab-visual" value="visual">Visuell</TabsTrigger>
          <TabsTrigger data-testid="tab-defaults" value="defaults">Standards</TabsTrigger>
          <TabsTrigger data-testid="tab-smtp" value="smtp">E-Mail (SMTP)</TabsTrigger>
          <TabsTrigger data-testid="tab-aposoft" value="aposoft">Aposoft</TabsTrigger>
          <TabsTrigger data-testid="tab-legal" value="legal">Recht</TabsTrigger>
        </TabsList>

        <TabsContent value="visual" className="space-y-4">
          <Card className="border-border/60">
            <CardHeader><CardTitle className="text-lg">Erscheinungsbild</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Systemname</Label><Input data-testid="brand-name" value={form.system_name || ""} onChange={(e) => setForm({ ...form, system_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Logo</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.logo_b64 && <img src={form.logo_b64} alt="logo" className="h-14 w-14 rounded-md border object-contain bg-white p-1" />}
                    <Input type="file" accept="image/*" onChange={(e) => readFile(e, "logo_b64")} data-testid="brand-logo" />
                  </div>
                </div>
                <div>
                  <Label>Favicon</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.favicon_b64 && <img src={form.favicon_b64} alt="fav" className="h-8 w-8 rounded border object-contain bg-white p-0.5" />}
                    <Input type="file" accept="image/*" onChange={(e) => readFile(e, "favicon_b64")} data-testid="brand-favicon" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Primärfarbe</Label>
                <Input type="color" value={form.primary_color || "#1D4ED8"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-24 p-1" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="defaults">
          <Card className="border-border/60">
            <CardHeader><CardTitle className="text-lg">Systemstandards</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Standardsprache</Label>
                <Select value={form.default_language || "de"} onValueChange={(v) => setForm({ ...form, default_language: v })}>
                  <SelectTrigger data-testid="brand-lang"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="de">Deutsch</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Abrechnungsmodus (global)</Label>
                <Select value={form.absence_mode_global || "days"} onValueChange={(v) => setForm({ ...form, absence_mode_global: v })}>
                  <SelectTrigger data-testid="brand-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="days">Tage</SelectItem><SelectItem value="hours">Stunden</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Standard-Puffer für Überstunden (Min.)</Label>
                  <Input type="number" value={form.overtime_buffer_minutes ?? 10} onChange={(e) => setForm({ ...form, overtime_buffer_minutes: parseInt(e.target.value || 10) })} />
                </div>
                <div>
                  <Label>Standard-Puffer für Verspätungen (Min.)</Label>
                  <Input type="number" value={form.late_buffer_minutes ?? 5} onChange={(e) => setForm({ ...form, late_buffer_minutes: parseInt(e.target.value || 5) })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="smtp" className="space-y-4">
          <Card className="border-border/60">
            <CardHeader><CardTitle className="text-lg">SMTP E-Mail-Versand</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Aktiviert</div>
                  <div className="text-sm text-slate-500">Wenn aus, werden keine System-Benachrichtigungen per E-Mail versendet.</div>
                </div>
                <Switch data-testid="smtp-enabled" checked={form.smtp?.enabled || false} onCheckedChange={(v) => setForm({ ...form, smtp: { ...form.smtp, enabled: v } })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>SMTP-Host</Label><Input data-testid="smtp-host" placeholder="smtp.example.com" value={form.smtp?.host || ""} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, host: e.target.value } })} /></div>
                <div><Label>Port</Label><Input data-testid="smtp-port" type="number" placeholder="587" value={form.smtp?.port || 587} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, port: parseInt(e.target.value || 587) } })} /></div>
                <div><Label>Benutzername</Label><Input data-testid="smtp-user" value={form.smtp?.username || ""} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, username: e.target.value } })} /></div>
                <div><Label>Passwort</Label><Input data-testid="smtp-pass" type="password" value={form.smtp?.password || ""} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, password: e.target.value } })} /></div>
                <div><Label>Absender-E-Mail</Label><Input data-testid="smtp-from" placeholder="portal@apotheke.de" value={form.smtp?.from_email || ""} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, from_email: e.target.value } })} /></div>
                <div><Label>Absender-Name</Label><Input data-testid="smtp-from-name" value={form.smtp?.from_name || ""} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, from_name: e.target.value } })} /></div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.smtp?.use_tls ?? true} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, use_tls: e.target.checked, use_ssl: false } })} /> STARTTLS</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.smtp?.use_ssl ?? false} onChange={(e) => setForm({ ...form, smtp: { ...form.smtp, use_ssl: e.target.checked, use_tls: false } })} /> SSL/TLS</label>
              </div>
              <SmtpTestForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aposoft" className="space-y-4">
          <Card className="border-border/60">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plug className="w-4 h-4" /> Aposoft-Schnittstelle</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Aktiviert</div>
                  <div className="text-sm text-slate-500">Synchronisation mit Aposoft (Warenwirtschaft) aktivieren.</div>
                </div>
                <Switch data-testid="aposoft-enabled" checked={form.aposoft?.enabled || false} onCheckedChange={(v) => setForm({ ...form, aposoft: { ...form.aposoft, enabled: v } })} />
              </div>
              <div><Label>API-URL</Label><Input data-testid="aposoft-url" placeholder="https://aposoft-api.example.com" value={form.aposoft?.api_url || ""} onChange={(e) => setForm({ ...form, aposoft: { ...form.aposoft, api_url: e.target.value } })} /></div>
              <div><Label>API-Key</Label><Input data-testid="aposoft-key" type="password" value={form.aposoft?.api_key || ""} onChange={(e) => setForm({ ...form, aposoft: { ...form.aposoft, api_key: e.target.value } })} /></div>
              <div><Label>Sync-Intervall (Minuten)</Label><Input data-testid="aposoft-interval" type="number" value={form.aposoft?.sync_interval_minutes || 60} onChange={(e) => setForm({ ...form, aposoft: { ...form.aposoft, sync_interval_minutes: parseInt(e.target.value || 60) } })} /></div>
              {form.aposoft?.last_sync && <div className="text-xs text-slate-500">Letzte Synchronisation: {new Date(form.aposoft.last_sync).toLocaleString("de-DE")}</div>}
              <Button data-testid="aposoft-test" variant="outline" onClick={async () => { try { await api.post("/branding/aposoft-test"); toast.success("Konfiguration OK"); } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); } }}>
                <TestTube className="w-4 h-4 mr-2" /> Konfiguration testen
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal">
          <Card className="border-border/60">
            <CardHeader><CardTitle className="text-lg">Rechtliche Texte</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Impressum</Label><Textarea rows={6} data-testid="brand-imprint" value={form.legal?.imprint || ""} onChange={(e) => setForm({ ...form, legal: { ...form.legal, imprint: e.target.value } })} /></div>
              <div><Label>Datenschutzerklärung</Label><Textarea rows={6} data-testid="brand-privacy" value={form.legal?.privacy || ""} onChange={(e) => setForm({ ...form, legal: { ...form.legal, privacy: e.target.value } })} /></div>
              <div><Label>AGB</Label><Textarea rows={6} data-testid="brand-terms" value={form.legal?.terms || ""} onChange={(e) => setForm({ ...form, legal: { ...form.legal, terms: e.target.value } })} /></div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
